import { lazy, Suspense, useEffect, type JSX } from "react";
import { Routes, Route, Navigate } from "react-router";
import {
  ADDRESS,
  COUPONS,
  DELETE_ACCOUNT,
  EDIT_PROFILE,
  MY_REVIEWS,
  ORDERS,
  OVERVIEW,
  PRIVACY_POLICY,
  PROFILE,
  TERMS_OF_SERVICE,
  WISHLIST,
  HELPWITHORDER,
  HELPNONORDER,
  RECENTISSUES,
  DELIVERYRELATED,
  LOGINRELATED,
  REFUNDRELATED,
  PAYEMENTRELATED,
  EMIRELATED,
  CANCELLATIONRELATED,
  OFFERSRELATED,
  CONTACTUS,
} from "./lib/Constants.ts";

import LoadingScreen from "./components/common/Loadingscreen.tsx";

/* ------------------- LAZY IMPORTS ------------------- */

const AddressPage = lazy(() => import("./(root)/cart/ExistingAddress.tsx"));
const Home = lazy(() => import("./(root)/home/page.tsx"));
const CartPage = lazy(() => import("./(root)/cart/page.tsx"));
const WishListPage = lazy(() => import("./(root)/wishlist/page.tsx"));
const Login = lazy(() => import("./(auth)/login/page.tsx"));
const ProductDetailPage = lazy(
  () => import("./(root)/product/ProductDetailPage.tsx")
);
const Register = lazy(() => import("./(auth)/register/page.tsx"));
const MyAccount = lazy(() => import("./(root)/profile/MyAccount.tsx"));
const EditProfile = lazy(() => import("./(root)/profile/EditProfile.tsx"));
const Addresses = lazy(() => import("./(root)/profile/Addresses.tsx"));
const Coupons = lazy(() => import("./(root)/profile/Coupons.tsx"));
const OrderAndReturns = lazy(
  () => import("./(root)/profile/OrderAndReturns.tsx")
);
const OtpContainer = lazy(() => import("./(auth)/otplogin/page.tsx"));
const OrderLisitngPage = lazy(
  () => import("./(root)/product/components/OrderLisitngPage.tsx")
);
const Layout = lazy(() => import("./(root)/profile/layouts/Layout.tsx"));
const CustomerSericeLayout = lazy(
  () => import("./(root)/customerservice/layouts/Layout.tsx")
);
const HelpWithOrder = lazy(
  () => import("./(root)/customerservice/layouts/help-order.tsx")
);
const NonOrderRelated = lazy(
  () => import("./(root)/customerservice/layouts/non-order-related.tsx")
);
const RecentIssues = lazy(
  () => import("./(root)/customerservice/layouts/recent-issues.tsx")
);
const BrandsPage = lazy(
  () => import("./components/common/layout/brands/page.tsx")
);
const OverviewPage = lazy(() => import("./(root)/profile/Overview.tsx"));
const MyReviewsPage = lazy(() => import("./(root)/profile/MyReviews.tsx"));
const BlogDetailPage = lazy(
  () => import("./(root)/home/components/blog/[id]/page.tsx")
);
const BlogListingPage = lazy(
  () => import("./(root)/home/components/blog/Bloglisting.tsx")
);
const CouponsPage = lazy(
  () => import("./(root)/cart/components/Couponspage.tsx")
);
const CategoryPage = lazy(
  () => import("./components/common/layout/categories/[id]/page.tsx")
);
const DeleteAccount = lazy(() => import("./(root)/profile/DeleteAccount.tsx"));
const TermAndCondition = lazy(
  () => import("./(root)/profile/TermAndCondition.tsx")
);
const PrivacyPolicy = lazy(() => import("./(root)/profile/PrivacyPolicy.tsx"));
const SearchResultsPage = lazy(() => import("./(root)/search/page.tsx"));
const AboutUs = lazy(() => import("./(root)/aboutus/about-us.tsx"));

const DeliveryRelated = lazy(
  () => import("./(root)/customerservice/layouts/delivery-related.tsx")
);
const LoginRelated = lazy(
  () => import("./(root)/customerservice/layouts/login-related.tsx")
);
const RefundRelated = lazy(
  () => import("./(root)/customerservice/layouts/refund-related.tsx")
);
const PaymentRelated = lazy(
  () => import("./(root)/customerservice/layouts/payment-related.tsx")
);
const EmiRelated = lazy(
  () => import("./(root)/customerservice/layouts/emi-related.tsx")
);
const CancellationRelated = lazy(
  () => import("./(root)/customerservice/layouts/cancellation-related.tsx")
);
const OffersRelated = lazy(
  () => import("./(root)/customerservice/layouts/offers-related.tsx")
);
const ContactUs = lazy(
  () => import("./(root)/customerservice/layouts/contact-us.tsx")
);

const OrderDetails = lazy(
  () => import("./(root)/profile/layouts/OrderDetail.tsx")
);

const Allcategories = lazy(
  () => import("./components/common/layout/categories/Allcategories.tsx")
);

const CartLoadingSkeleton = lazy(
  () => import("./(root)/cart/components/Loading.tsx")
);

const MostWantedPageWrapper = lazy(
  () => import("./(root)/home/components/MostWantedPageWrapper.tsx")
);

const NewArrivalsPageWrapper = lazy(
  () => import("./(root)/home/components/NewArrivalsPageWrapper.tsx")
);

const SimilarProductsPageWrapper = lazy(
  () => import("./(root)/home/components/SimilarProductsPageWrapper.tsx")
);

const PolicyDisplay = lazy(
  () => import("./components/policies/CancellationPolicy.tsx")
);

const ShippingPolicy = lazy(
  () => import("./components/policies/ShippingPolicy.tsx")
);

const OfferCardPage = lazy(() => import("./(root)/offers/OfferCardPage.tsx"));
const OfferPage = lazy(() => import("./(root)/offers/OfferPage.tsx"));
const ViewAllOffers = lazy(() => import("./(root)/offers/viewAlloffers.tsx"));
const ComboOfferDetailPage = lazy(
  () => import("./(root)/product/ComboOfferDetailPage.tsx")
);


/* ------------------- TITLE WRAPPER ------------------- */

const TitleWrapper = ({
  title,
  children,
}: {
  title: string;
  children: JSX.Element;
}) => {
  useEffect(() => {
    document.title = `${title} | Care mall`;
  }, [title]);

  return children;
};

/* ------------------- ROUTES ------------------- */

const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        <Route
          path="/"
          element={
            <TitleWrapper title="Home">
              <Home />
            </TitleWrapper>
          }
        />

        <Route
          path="/cart"
          element={
            <TitleWrapper title="Cart">
              <Suspense fallback={<CartLoadingSkeleton />}>
                <CartPage />
              </Suspense>
            </TitleWrapper>
          }
        />

        <Route
          path="/coupons"
          element={
            <TitleWrapper title="Coupons">
              <CouponsPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/most-wanted"
          element={
            <TitleWrapper title="Most Wanted">
              <MostWantedPageWrapper />
            </TitleWrapper>
          }
        />

        <Route
          path="/new-arrival"
          element={
            <TitleWrapper title="New Arrivals">
              <NewArrivalsPageWrapper />
            </TitleWrapper>
          }
        />

        <Route
          path="/similar-products/:productId"
          element={
            <TitleWrapper title="Similar Products">
              <SimilarProductsPageWrapper />
            </TitleWrapper>
          }
        />

        <Route
          path="/offer-card/:id"
          element={
            <TitleWrapper title="Offers">
              <OfferCardPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/offers/:id"
          element={
            <TitleWrapper title="Offers">
              <OfferPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/all-offers"
          element={
            <TitleWrapper title="Offers">
              <ViewAllOffers />
            </TitleWrapper>
          }
        />

        <Route
          path="/categories/:id"
          element={
            <TitleWrapper title="Categories">
              <CategoryPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/categories"
          element={
            <TitleWrapper title="Categories">
              <Allcategories />
            </TitleWrapper>
          }
        />

        <Route
          path="/brands"
          element={
            <TitleWrapper title="Brands">
              <BrandsPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/brands/:brandId"
          element={
            <TitleWrapper title="Brands">
              <BrandsPage />
            </TitleWrapper>
          }
        />

        <Route
          path={WISHLIST}
          element={
            <TitleWrapper title="Wishlist">
              <WishListPage />
            </TitleWrapper>
          }
        />

        <Route
          path={TERMS_OF_SERVICE}
          element={
            <TitleWrapper title="Terms & Conditions">
              <TermAndCondition />
            </TitleWrapper>
          }
        />

        <Route
          path={PRIVACY_POLICY}
          element={
            <TitleWrapper title="Privacy Policy">
              <PrivacyPolicy />
            </TitleWrapper>
          }
        />

        <Route
          path="/login"
          element={
            <TitleWrapper title="Login">
              <Login />
            </TitleWrapper>
          }
        />

        <Route
          path="/otplogin"
          element={
            <TitleWrapper title="OTP Login">
              <OtpContainer />
            </TitleWrapper>
          }
        />

        <Route path="/forgot" element={<Navigate to="/login" replace />} />

        <Route
          path="/register"
          element={
            <TitleWrapper title="Register">
              <Register />
            </TitleWrapper>
          }
        />

        <Route
          path="/checkout"
          element={
            <TitleWrapper title="Checkout">
              <AddressPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/product/:id"
          element={
            <TitleWrapper title="Product Details">
              <ProductDetailPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/product/:id/r/:linkCode"
          element={
            <TitleWrapper title="Product Details">
              <ProductDetailPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/combo-offer/:slug"
          element={
            <TitleWrapper title="Combo Offer Details">
              <ComboOfferDetailPage />
            </TitleWrapper>
          }
        />


        <Route
          path="/blog/:id"
          element={
            <TitleWrapper title="Blog">
              <BlogDetailPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/blogs"
          element={
            <TitleWrapper title="Blogs">
              <BlogListingPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/search"
          element={
            <TitleWrapper title="Search Results">
              <SearchResultsPage key={location.search} />
            </TitleWrapper>
          }
        />

        <Route
          path="/order/:id"
          element={
            <TitleWrapper title="Order">
              <OrderLisitngPage />
            </TitleWrapper>
          }
        />

        <Route
          path="/order/order-detail/:id"
          element={
            <TitleWrapper title="Order Details">
              <OrderDetails />
            </TitleWrapper>
          }
        />

        <Route
          path="/about-us"
          element={
            <TitleWrapper title="About Us">
              <AboutUs />
            </TitleWrapper>
          }
        />

        {/* PROFILE LAYOUT */}
        <Route element={<Layout />}>
          <Route
            path={OVERVIEW}
            element={
              <TitleWrapper title="Overview">
                <OverviewPage />
              </TitleWrapper>
            }
          />

          <Route
            path={PROFILE}
            element={
              <TitleWrapper title="My Account">
                <MyAccount />
              </TitleWrapper>
            }
          />

          <Route
            path={EDIT_PROFILE}
            element={
              <TitleWrapper title="Edit Profile">
                <EditProfile />
              </TitleWrapper>
            }
          />

          <Route
            path={DELETE_ACCOUNT}
            element={
              <TitleWrapper title="Delete Account">
                <DeleteAccount />
              </TitleWrapper>
            }
          />

          <Route
            path={ADDRESS}
            element={
              <TitleWrapper title="Addresses">
                <Addresses />
              </TitleWrapper>
            }
          />

          <Route
            path={COUPONS}
            element={
              <TitleWrapper title="Coupons">
                <Coupons />
              </TitleWrapper>
            }
          />

          <Route
            path={ORDERS}
            element={
              <TitleWrapper title="Orders & Returns">
                <OrderAndReturns />
              </TitleWrapper>
            }
          />

          <Route
            path={MY_REVIEWS}
            element={
              <TitleWrapper title="My Reviews">
                <MyReviewsPage />
              </TitleWrapper>
            }
          />
        </Route>

        {/* CUSTOMER SERVICE */}
        <Route element={<CustomerSericeLayout />}>
          <Route
            path={HELPWITHORDER}
            element={
              <TitleWrapper title="Help with Order">
                <HelpWithOrder />
              </TitleWrapper>
            }
          />

          <Route
            path={HELPNONORDER}
            element={
              <TitleWrapper title="Non Order Issues">
                <NonOrderRelated />
              </TitleWrapper>
            }
          />

          <Route
            path={RECENTISSUES}
            element={
              <TitleWrapper title="Recent Issues">
                <RecentIssues />
              </TitleWrapper>
            }
          />

          <Route
            path={DELIVERYRELATED}
            element={
              <TitleWrapper title="Delivery Help">
                <DeliveryRelated />
              </TitleWrapper>
            }
          />

          <Route
            path={LOGINRELATED}
            element={
              <TitleWrapper title="Login Related">
                <LoginRelated />
              </TitleWrapper>
            }
          />

          <Route
            path={REFUNDRELATED}
            element={
              <TitleWrapper title="Refund Help">
                <RefundRelated />
              </TitleWrapper>
            }
          />

          <Route
            path={PAYEMENTRELATED}
            element={
              <TitleWrapper title="Payment Help">
                <PaymentRelated />
              </TitleWrapper>
            }
          />

          <Route
            path={EMIRELATED}
            element={
              <TitleWrapper title="EMI Help">
                <EmiRelated />
              </TitleWrapper>
            }
          />

          <Route
            path={CANCELLATIONRELATED}
            element={
              <TitleWrapper title="Cancellation Help">
                <CancellationRelated />
              </TitleWrapper>
            }
          />

          <Route
            path="/cancel-policy"
            element={
              <TitleWrapper title="Cancellation Policy">
                <PolicyDisplay />
              </TitleWrapper>
            }
          />

          <Route
            path="/shipping-policy"
            element={
              <TitleWrapper title="Shipping Policy">
                <ShippingPolicy />
              </TitleWrapper>
            }
          />

          <Route
            path={OFFERSRELATED}
            element={
              <TitleWrapper title="Offers Help">
                <OffersRelated />
              </TitleWrapper>
            }
          />

          <Route
            path={CONTACTUS}
            element={
              <TitleWrapper title="Contact Us">
                <ContactUs />
              </TitleWrapper>
            }
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
